import { useEffect, useState } from 'react';
import { AlertTriangle, ArrowRight, GitMerge, Loader2, Receipt as ReceiptIcon, User, X } from 'lucide-react';

export interface DueReceiptSummary {
  id: string;
  receiptNumber: string;
  dueAmount?: number;
  totalAmount?: number;
  paidAmount?: number;
  createdAt?: string;
}

interface DueReceiptPromptModalProps {
  open: boolean;
  patientName?: string;
  /** Shown so the counter can confirm the record was matched on the number. */
  patientMobile?: string;
  /** Unpaid/partly paid receipts of the same patient, oldest first. */
  receipts: DueReceiptSummary[];
  /**
   * `create` — a brand-new receipt is being written, so the action opens the
   * outstanding receipt to add the test there and skipping means "create a
   * separate receipt". `edit` — consolidating the patient's unpaid receipts into
   * the receipt already open, so skipping means "keep editing this one".
   */
  mode: 'create' | 'edit';
  /** Edit mode: the receipt being edited — the destination of a merge. */
  targetReceiptNumber?: string;
  /**
   * Edit mode only. Folds every listed receipt into the target receipt (tests,
   * accessories and collected payments move across; sources go to Trash).
   * Reject with an `Error` to surface the reason in the dialog.
   */
  onMerge?: () => Promise<void>;
  onOpenReceipt: (receiptId: string) => void;
  onSkip: () => void;
}

const formatMoney = (value: number) => `৳${Number(value || 0).toLocaleString()}`;

const formatDate = (value?: string) => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

/**
 * Prompt shown when the patient already has diagnostic receipts with an
 * outstanding balance.
 *
 * - On the edit page the receipts are consolidated: they are merged into the
 *   receipt being edited so one document carries all the tests and the balance.
 * - On the new-receipt page the outstanding receipt is opened so the new test
 *   is added to it instead of raising a second receipt.
 */
export function DueReceiptPromptModal({
  open,
  patientName,
  patientMobile,
  receipts,
  mode,
  targetReceiptNumber,
  onMerge,
  onOpenReceipt,
  onSkip,
}: DueReceiptPromptModalProps) {
  // Merging is deliberately two-step: the confirmation spells out exactly what
  // moves where, because the source receipts are parked in the Trash afterwards.
  const [confirmingMerge, setConfirmingMerge] = useState(false);
  const [merging, setMerging] = useState(false);
  const [mergeError, setMergeError] = useState<string | null>(null);

  useEffect(() => {
    setConfirmingMerge(false);
    setMerging(false);
    setMergeError(null);
  }, [open]);

  const handleMerge = async () => {
    setMerging(true);
    setMergeError(null);
    try {
      await onMerge?.();
    } catch (error: any) {
      setMergeError(error?.message || 'Failed to merge the receipts. Please try again.');
      setMerging(false);
    }
  };

  if (!open || receipts.length === 0) return null;

  const canMerge = mode === 'edit' && !!onMerge;
  const totalDue = receipts.reduce((sum, receipt) => sum + (Number(receipt.dueAmount) || 0), 0);
  const totalCollected = receipts.reduce(
    (sum, receipt) => sum + (Number(receipt.paidAmount) || 0),
    0,
  );
  const primaryReceipt = receipts[0];
  const skipHint =
    mode === 'create'
      ? 'Skip and create a separate receipt'
      : 'Skip and keep editing the receipt already open';

  const receiptRow = (receipt: DueReceiptSummary, index: number) => {
    const content = (
      <>
        <div className="p-1.5 rounded-lg bg-primary/10 shrink-0">
          <ReceiptIcon size={14} className="text-primary" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-mono text-sm font-medium text-text truncate">{receipt.receiptNumber}</p>
          <p className="text-xs text-text-muted">
            {[
              formatDate(receipt.createdAt),
              Number(receipt.paidAmount) > 0
                ? `Paid ${formatMoney(Number(receipt.paidAmount))}`
                : 'Nothing paid yet',
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
        </div>
        <div className="text-right shrink-0">
          <p className="text-sm font-bold text-danger">
            {formatMoney(Number(receipt.dueAmount) || 0)}
          </p>
          <p className="text-[10px] uppercase tracking-wide text-text-muted">Due</p>
        </div>
        {!canMerge && <ArrowRight size={15} className="text-text-muted shrink-0" />}
      </>
    );

    const className = `w-full flex items-center gap-3 rounded-lg border px-3 py-2.5 text-left ${
      canMerge
        ? index === 0
          ? 'border-border'
          : 'border-border bg-background/50'
        : `transition-colors hover:bg-background ${
            index === 0 ? 'border-primary/40 bg-primary/5' : 'border-border'
          }`
    }`;

    if (canMerge) {
      return (
        <div key={receipt.id} className={className}>
          {content}
        </div>
      );
    }

    return (
      <button
        key={receipt.id}
        type="button"
        onClick={() => onOpenReceipt(receipt.id)}
        className={className}
      >
        {content}
      </button>
    );
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      onClick={onSkip}
    >
      <div
        className="bg-surface border border-border rounded-xl shadow-xl w-full max-w-lg"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="p-5 space-y-4 max-h-[80vh] overflow-y-auto">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-warning/10 shrink-0">
              <AlertTriangle size={20} className="text-warning" />
            </div>
            <div className="space-y-1 min-w-0 flex-1">
              <h3 className="text-base font-semibold text-text">Outstanding Receipt Found</h3>
              <p className="text-sm text-text-muted">
                {canMerge
                  ? 'This patient has other receipts with an unpaid balance. Merge them into this receipt so one document carries all the tests and the balance.'
                  : 'This patient already has a diagnostic receipt with an unpaid balance. Add the new test to that receipt so the tests and the balance stay together.'}
              </p>
            </div>
            <button
              type="button"
              onClick={onSkip}
              className="p-1.5 -mt-1 -mr-1 rounded-lg text-text-muted hover:text-text hover:bg-background transition-colors shrink-0"
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg border border-border bg-background px-3 py-2 text-xs">
            <span className="inline-flex items-center gap-1.5 text-text">
              <User size={13} className="text-text-muted" />
              {patientName || 'Selected patient'}
            </span>
            {patientMobile && <span className="font-mono text-text-muted">{patientMobile}</span>}
            <span className="ml-auto font-semibold text-danger">
              Total due {formatMoney(totalDue)}
            </span>
          </div>

          <div className="space-y-2">{receipts.map(receiptRow)}</div>

          {canMerge && confirmingMerge && (
            <div className="rounded-lg border border-warning/30 bg-warning/5 p-3 space-y-1">
              <p className="text-xs text-text">
                Merge {receipts.length} {receipts.length === 1 ? 'receipt' : 'receipts'} into{' '}
                <span className="font-mono font-medium">{targetReceiptNumber}</span>? Their tests
                and {formatMoney(totalCollected)} already collected move here, and the other{' '}
                {receipts.length === 1 ? 'receipt is' : 'receipts are'} moved to Trash.
              </p>
              {mergeError && <p className="text-xs text-danger">{mergeError}</p>}
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-3 px-5 py-4 border-t border-border">
          <button
            type="button"
            onClick={onSkip}
            disabled={merging}
            title={skipHint}
            className="px-4 py-2 rounded-lg border border-border bg-surface text-sm font-medium text-text hover:bg-background transition-colors disabled:opacity-50"
          >
            Skip
          </button>

          {canMerge ? (
            confirmingMerge ? (
              <>
                <button
                  type="button"
                  onClick={() => {
                    setConfirmingMerge(false);
                    setMergeError(null);
                  }}
                  disabled={merging}
                  className="px-4 py-2 rounded-lg border border-border bg-surface text-sm font-medium text-text hover:bg-background transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleMerge}
                  disabled={merging}
                  className="px-4 py-2 rounded-lg bg-warning text-white text-sm font-medium hover:bg-warning-dark transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  {merging ? <Loader2 size={15} className="animate-spin" /> : <GitMerge size={15} />}
                  {merging ? 'Merging...' : 'Yes, merge'}
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => setConfirmingMerge(true)}
                className="px-4 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary-dark transition-colors flex items-center gap-2"
              >
                <GitMerge size={15} />
                Merge {receipts.length} into {targetReceiptNumber}
              </button>
            )
          ) : (
            <button
              type="button"
              onClick={() => onOpenReceipt(primaryReceipt.id)}
              className="px-4 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary-dark transition-colors flex items-center gap-2"
            >
              Add Test to {primaryReceipt.receiptNumber}
              <ArrowRight size={15} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
